import type { GeoCoordinates, AddressDraft } from '@repo/domain';
import { createStore, persist } from '@repo/zustand';
import { isApplicationMode } from '@repo/env-variables';
import { isValidSpotName } from '@repo/domain-service';

import { MAX_NUMBER_OF_SPOTS } from '@shared/constants/parkingSpots';
import { formatAddressDraftLine } from '@shared/utils/formatAddressDraftLine';
import { resizeSpotsArray } from '@shared/utils/resizeSpotsArray';

type OwnParkingState = {
  id?: string;
  address: AddressDraft;
};

interface State {
  // Parking id and address.
  ownParking: OwnParkingState;
  setOwnParkingId: (id: string) => void;
  setOwnParkingAddressLine: (line: string) => void;
  setOwnParkingPostalCode: (postalCode: string) => void;
  setOwnParkingCity: (city: string) => void;
  setOwnParkingCoordinates: (coordinates: GeoCoordinates) => void;
  setOwnParkingAddress: (address: AddressDraft) => void;

  // Spots.
  spots: string[];
  addSpot: (name?: string) => void;
  deleteSpot: (index: number) => void;
  renameSpot: (index: number, name: string) => void;
  addManySpots: (names: string[]) => void;
  renameSpots: (indexFrom: number, spotNames: string[]) => void;
  setSpotsLength: (newLength: number) => void;

  // Other.
  reset: () => void;
}

const createDefaultOwnParking = (): OwnParkingState => ({
  id: undefined,
  address: {
    line: '',
    postalCode: '',
    city: '',
    coordinates: undefined,
    resolved: undefined,
  },
});
const createDefaultSpots = (): string[] => ['1', '2', '3'];

export const useParkingCreateStore = createStore<State>({
  name: 'ParkingCreateStore',
  isDev: isApplicationMode('dev'),
})(
  persist(
    // Parking id and address.
    (set): State => ({
      ownParking: createDefaultOwnParking(),
      setOwnParkingId(id: string): void {
        set((state) => ({
          ownParking: { ...state.ownParking, id },
        }));
      },
      setOwnParkingAddressLine(line: string): void {
        set((state) => ({
          ownParking: {
            ...state.ownParking,
            address: { ...state.ownParking.address, line, coordinates: undefined, resolved: undefined },
          },
        }));
      },
      setOwnParkingPostalCode(postalCode: string): void {
        set((state) => ({
          ownParking: {
            ...state.ownParking,
            address: { ...state.ownParking.address, postalCode, coordinates: undefined, resolved: undefined },
          },
        }));
      },
      setOwnParkingCity(city: string): void {
        set((state) => ({
          ownParking: {
            ...state.ownParking,
            address: { ...state.ownParking.address, city, coordinates: undefined, resolved: undefined },
          },
        }));
      },
      setOwnParkingCoordinates(coordinates: GeoCoordinates): void {
        set((state) => ({
          ownParking: {
            ...state.ownParking,
            address: { ...state.ownParking.address, coordinates, resolved: undefined },
          },
        }));
      },
      setOwnParkingAddress(address: AddressDraft): void {
        set((state) => ({
          ownParking: { ...state.ownParking, address },
        }));
      },

      // Spots.
      spots: createDefaultSpots(),
      addSpot(name?: string): void {
        set((state) => ({
          spots: [...state.spots, name ?? (state.spots.length + 1).toString()],
        }));
      },
      deleteSpot(index: number): void {
        set((state) => ({
          spots: state.spots.filter((_, position) => position !== index),
        }));
      },
      renameSpot(index: number, name: string): void {
        set((state) => ({
          spots: state.spots.map((spot, position) => (position === index ? name : spot)),
        }));
      },
      addManySpots(names: string[]): void {
        set((state) => ({
          spots: [...state.spots, ...names],
        }));
      },
      renameSpots(indexFrom: number, spotNames: string[]): void {
        set((state) => ({
          spots: state.spots.map((spot, position) => {
            const offset = position - (indexFrom - 1);
            return offset >= 0 && offset < spotNames.length ? spotNames[offset] : spot;
          }),
        }));
      },
      setSpotsLength(newLength: number): void {
        set((state) => ({ spots: resizeSpotsArray(state.spots, newLength) }));
      },

      // Other.
      reset(): void {
        set({ ownParking: createDefaultOwnParking(), spots: createDefaultSpots() });
      },
    }),
    { name: 'createParking-storage-v3' }
  )
);

export const selectAddressLine = (state: State): string | undefined => {
  if (!state.ownParking.address.city || !state.ownParking.address.line) return undefined;
  return formatAddressDraftLine(state.ownParking.address);
};

export const selectIsEmpty = (state: State): boolean => {
  const defaultOwnParking = createDefaultOwnParking();
  const defaultSpots = createDefaultSpots();
  return (
    JSON.stringify(state.ownParking) === JSON.stringify(defaultOwnParking) &&
    JSON.stringify(state.spots) === JSON.stringify(defaultSpots)
  );
};

export const selectAreSpotsValid = (state: State): boolean => {
  const { spots } = state;
  if (spots.length < 1 || spots.length > MAX_NUMBER_OF_SPOTS) return false;
  if (spots.some((name) => name.trim().length === 0)) return false;
  if (spots.some((name) => !isValidSpotName(name))) return false;
  if (new Set(spots).size !== spots.length) return false;
  return true;
};
